import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Dropdown, Spinner } from '@/components/common';
import { useResource } from '@/hooks';
import { currentLanguage, LOCALE_TAG } from '@/i18n';
import { errorMessage } from '@/lib/api-client';
import type { ReportSummary } from '@/types/api';
import { type Code, codeLabel } from '@/utils/dental';
import { formatRsd } from '../chart/components';
import { useDoctor } from '../doctors';
import { reportsApi } from './api';

const thisYear = () => new Date().getFullYear();

function monthName(month: number, style: 'long' | 'short'): string {
  return new Intl.DateTimeFormat(LOCALE_TAG[currentLanguage()], { month: style }).format(
    new Date(2026, month - 1, 15)
  );
}

const percent = (part: number, whole: number) =>
  whole ? `${Math.round((part / whole) * 100)} %` : '—';

type Row = { key: string; label: string; value: number; text: string; selected?: boolean };

/** Rows with a bar each, its length relative to the largest value; the value is always written out. */
function BarList({
  rows,
  label,
  onSelect,
}: {
  rows: Row[];
  label: string;
  onSelect?: (key: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="bar-list" aria-label={label}>
      {rows.map((r) => {
        const inner = (
          <>
            <span className="bar-list__label">{r.label}</span>
            <span className="bar-list__track" aria-hidden="true">
              {r.value > 0 && (
                <span className="bar-list__bar" style={{ width: `${(r.value / max) * 100}%` }} />
              )}
            </span>
            <span className="bar-list__value">{r.text}</span>
          </>
        );
        return (
          <li key={r.key}>
            {onSelect ? (
              <button
                type="button"
                className="bar-list__row"
                aria-pressed={!!r.selected}
                onClick={() => onSelect(r.key)}
              >
                {inner}
              </button>
            ) : (
              <span className="bar-list__row">{inner}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function workLabel(t: (key: string) => string, code: string | null): string {
  return code ? codeLabel(code as Code) : t('reports.noWork');
}

function Results({
  data,
  onMonth,
}: {
  data: ReportSummary;
  onMonth: (month: number | null) => void;
}) {
  const { t } = useTranslation();
  const done = data.workTypes.filter((w) => w.count > 0);
  const doneTotal = done.reduce((sum, w) => sum + w.count, 0);
  const paid = [...data.workTypes]
    .filter((w) => w.revenueRsd > 0)
    .sort((a, b) => b.revenueRsd - a.revenueRsd);
  const paidTotal = paid.reduce((sum, w) => sum + w.revenueRsd, 0);
  const period = data.month ? `${monthName(data.month, 'long')} ${data.year}` : String(data.year);
  const monthRevenue = data.month ? data.months[data.month - 1]?.revenueRsd : null;

  return (
    <>
      <div className="report-stats">
        <section className="card report-stat">
          <span className="report-stat__label">
            {t('reports.revenueYear', { year: data.year })}
          </span>
          <strong className="report-stat__value">{formatRsd(data.totalRsd)}</strong>
          <span className="small muted">{t('reports.visits', { count: data.visits })}</span>
        </section>
        {data.month && (
          <section className="card report-stat">
            <span className="report-stat__label">{period}</span>
            <strong className="report-stat__value">{formatRsd(monthRevenue ?? 0)}</strong>
            <span className="small muted">
              {t('reports.visits', { count: data.months[data.month - 1]?.visits ?? 0 })}
            </span>
          </section>
        )}
      </div>

      <div className="reports-grid">
        <section className="card">
          <div className="card__head">
            <h2>{t('reports.byMonth')}</h2>
          </div>
          <BarList
            label={t('reports.byMonth')}
            rows={data.months.map((m) => ({
              key: String(m.month),
              label: monthName(m.month, 'short'),
              value: m.revenueRsd,
              text: formatRsd(m.revenueRsd),
              selected: data.month === m.month,
            }))}
            onSelect={(key) => onMonth(data.month === Number(key) ? null : Number(key))}
          />
          <p className="small muted">{t('reports.pickMonth')}</p>
        </section>

        <section className="card">
          <div className="card__head">
            <h2>{t('reports.byYear')}</h2>
          </div>
          {data.years.length ? (
            <BarList
              label={t('reports.byYear')}
              rows={data.years.map((y) => ({
                key: String(y.year),
                label: String(y.year),
                value: y.revenueRsd,
                text: formatRsd(y.revenueRsd),
              }))}
            />
          ) : (
            <p className="small muted">{t('reports.empty')}</p>
          )}
        </section>

        <section className="card">
          <div className="card__head">
            <h2>{t('reports.workShare', { period })}</h2>
          </div>
          {done.length ? (
            <BarList
              label={t('reports.workShare', { period })}
              rows={done.map((w) => ({
                key: String(w.code),
                label: workLabel(t, w.code),
                value: w.count,
                text: `${w.count} · ${percent(w.count, doneTotal)}`,
              }))}
            />
          ) : (
            <p className="small muted">{t('reports.empty')}</p>
          )}
        </section>

        <section className="card">
          <div className="card__head">
            <h2>{t('reports.workRevenue', { period })}</h2>
          </div>
          {paid.length ? (
            <BarList
              label={t('reports.workRevenue', { period })}
              rows={paid.map((w) => ({
                key: String(w.code),
                label: workLabel(t, w.code),
                value: w.revenueRsd,
                text: `${formatRsd(w.revenueRsd)} · ${percent(w.revenueRsd, paidTotal)}`,
              }))}
            />
          ) : (
            <p className="small muted">{t('reports.empty')}</p>
          )}
          <p className="small muted">{t('reports.splitNote')}</p>
        </section>
      </div>
    </>
  );
}

/** Revenue by month and year, and the work done, for the clinic or one doctor. */
export function ReportsPage() {
  const { t } = useTranslation();
  const { all: doctors } = useDoctor();
  const [year, setYear] = useState(thisYear());
  const [month, setMonth] = useState<number | null>(null);
  const [dentistId, setDentistId] = useState<string | null>(null);
  const report = useResource(
    () => reportsApi.summary({ year, month, dentistId }),
    `${year}|${month}|${dentistId}`
  );

  const years = new Set([thisYear(), year, ...(report.data?.years.map((y) => y.year) ?? [])]);
  return (
    <div className="stack reports">
      <div className="page-head">
        <div className="page-head__title">
          <h1>{t('reports.title')}</h1>
        </div>
      </div>

      <div className="filters">
        <Dropdown
          ariaLabel={t('reports.year')}
          value={year}
          options={[...years].sort((a, b) => b - a).map((y) => ({ value: y, label: String(y) }))}
          onChange={(y) => {
            setYear(y);
            setMonth(null);
          }}
        />
        <Dropdown
          ariaLabel={t('reports.month')}
          value={month ?? 0}
          options={[
            { value: 0, label: t('reports.wholeYear') },
            ...Array.from({ length: 12 }, (_, i) => ({
              value: i + 1,
              label: monthName(i + 1, 'long'),
            })),
          ]}
          onChange={(m) => setMonth(m || null)}
        />
        <Dropdown
          ariaLabel={t('calendar.filter')}
          value={dentistId ?? ''}
          options={[
            { value: '', label: t('reports.allDoctors') },
            ...doctors.map((d) => ({ value: d.id, label: d.name })),
          ]}
          onChange={(id) => setDentistId(id || null)}
        />
      </div>

      {report.error ? (
        <Banner
          tone="error"
          action={
            <button type="button" className="btn btn--sm" onClick={() => report.reload()}>
              {t('common.retry')}
            </button>
          }
        >
          {errorMessage(report.error)}
        </Banner>
      ) : report.data ? (
        <Results data={report.data} onMonth={setMonth} />
      ) : (
        <Spinner />
      )}
    </div>
  );
}
